import React, { useState } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import { Heading, ServerErrorMessage } from '@peracto/peracto-ui'

import useCategories from './useCategories'
import { asTree, flatten, needsReview } from './categoryTree'

const Status = ({ active }) => (
    <span className={classNames('badge', 'badge-pill', active ? 'badge-success' : 'badge-secondary')}>
        {active ? 'Active' : 'Switched off'}
    </span>
)

const INDENT = { 1: 'font-weight-bold', 2: 'pl-4', 3: 'pl-5 ml-3' }

const Review = ({ category }) => (
    <>
        {category.needsReview && (
            <span className="badge badge-pill badge-warning mr-1" title={category.reviewNote || ''}>
                Category
            </span>
        )}
        {category.genericArticlesNeedingReviewCount > 0 && (
            <span className="badge badge-pill badge-warning">
                {category.genericArticlesNeedingReviewCount} GenArt
                {category.genericArticlesNeedingReviewCount === 1 ? '' : 's'}
            </span>
        )}
    </>
)

const CategoryRow = ({ category }) => (
    <tr data-testid={`vehicle-category-${category.slug}`}>
        <td className={INDENT[category.depth]}>
            <Link to={category['@id']}>{category.name}</Link>
        </td>
        <td className="text-muted">{category.slug}</td>
        <td>{category.position}</td>
        <td>{category.genericArticleCount}</td>
        <td>
            <Review category={category} />
        </td>
        <td>
            <Status active={category.active} />
        </td>
    </tr>
)

const reviewSummary = rows => {
    const categories = rows.filter(row => row.needsReview).length
    const links = rows.reduce((total, row) => total + (row.genericArticlesNeedingReviewCount || 0), 0)

    return `${categories} categories and ${links} GenArt links need review.`
}

const CategoryList = () => {
    const { categories, error } = useCategories()
    const [onlyReview, setOnlyReview] = useState(false)
    const rows = categories === null ? [] : flatten(asTree(categories))
    const shown = onlyReview ? rows.filter(needsReview) : rows

    if (error) {
        return <ServerErrorMessage />
    }

    return (
        <>
            <Helmet>
                <title>Vehicle Categories | Peracto</title>
            </Helmet>
            <Heading name="Vehicle Categories">
                <div className="d-flex align-items-center ml-2">
                    <Link
                        className="btn btn-primary"
                        to="/vehicle-categories/add"
                        data-testid="add-vehicle-category"
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
                        Add Category
                    </Link>
                </div>
            </Heading>
            <p className="text-muted">
                The categories customers browse after choosing a vehicle, up to three levels deep. Each one
                lists the TecDoc parts of its GenArts that fit the vehicle, and those of the categories under
                it. Changes show on the storefront within five minutes.
            </p>
            <div className="d-flex align-items-center mb-3">
                <label className="mb-0 mr-3" htmlFor="only-review">
                    <input
                        id="only-review"
                        type="checkbox"
                        className="mr-2"
                        checked={onlyReview}
                        onChange={event => setOnlyReview(event.target.checked)}
                        data-testid="only-review"
                    />
                    Only what needs review
                </label>
                {categories !== null && <span className="text-muted">{reviewSummary(rows)}</span>}
            </div>
            <div className="card">
                <div className="card-body">
                    {categories === null ? (
                        'Loading...'
                    ) : (
                        <table className="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Slug</th>
                                    <th>Position</th>
                                    <th>GenArts</th>
                                    <th>Review</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map(category => (
                                    <CategoryRow key={category['@id']} category={category} />
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    )
}

export default CategoryList
